import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';

import { CheckCircle, Gift, Sparkles } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import ComingSoon from '@/components/ComingSoon';
import { usePrelaunch } from '@/hooks/usePrelaunch';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function GiftQuestionnaire() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { prelaunch } = usePrelaunch();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [giftGiverName, setGiftGiverName] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  // Dynamic OG for shared gift-form links
  useDocumentMeta({
    title: giftGiverName
      ? `Help ${giftGiverName} find the perfect gift, Mementa`
      : 'Gift Discovery, Mementa',
    description: giftGiverName
      ? `${giftGiverName} wants to find the perfect gift for you. Answer a few fun questions on Mementa.`
      : 'Answer a few fun questions and help someone find the perfect gift for you.',
  });
  
  const [responses, setResponses] = useState({
    interests: [],
    hobbies: [],
    personality: '',
    favorite_things: '',
    dream_gift: '',
    budget: '',
    occasion: '',
    style: '',
    avoid: ''
  });

  const fetchQuestionnaire = useCallback(async () => {
    try {
      const response = await api.get(`/questionnaire/${shareToken}`);
      setGiftGiverName(response.data.gift_giver_name);
      
      if (response.data.completed) {
        // Already completed - show message
        setShowRecommendations(true);
      }
    } catch (error) {
      toast.error('Invalid or expired link');
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  }, [shareToken, navigate]);

  useEffect(() => {
    fetchQuestionnaire();
  }, [fetchQuestionnaire]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await api.post(`/questionnaire/${shareToken}/submit`, responses);
      setRecommendations(response.data.recommendations || []);
      setShowRecommendations(true);
      toast.success('Thank you! Here are some gift ideas for you! 🎁');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const questions = [
    {
      title: "Let's start with the fun stuff!",
      subtitle: "What are you really into these days?",
      field: 'interests',
      type: 'multiselect',
      options: ['Reading', 'Gaming', 'Cooking', 'Fitness', 'Art & Crafts', 'Music', 'Tech', 'Travel', 'Fashion', 'Photography', 'Gardening', 'Sports']
    },
    {
      title: "How do you like to spend your free time?",
      subtitle: "Any hobbies or activities that bring you joy?",
      field: 'hobbies',
      type: 'textarea',
      placeholder: 'e.g., I love painting on weekends, playing guitar, or exploring new coffee shops...'
    },
    {
      title: "If you had to describe yourself in a few words...",
      subtitle: "What words come to mind?",
      field: 'personality',
      type: 'multiselect',
      options: ['Creative', 'Practical', 'Adventurous', 'Thoughtful', 'Minimalist', 'Tech-savvy', 'Traditional', 'Trendy', 'Eco-conscious']
    },
    {
      title: "What's something you've been wanting lately?",
      subtitle: "No pressure - just curious what's on your wishlist!",
      field: 'dream_gift',
      type: 'textarea',
      placeholder: 'e.g., A cozy reading nook setup, new running shoes, or maybe a cooking gadget...'
    },
    {
      title: "What's your style?",
      subtitle: "Help us understand your vibe",
      field: 'style',
      type: 'multiselect',
      options: ['Minimalist', 'Colorful & Bold', 'Classic & Timeless', 'Modern & Sleek', 'Rustic & Natural', 'Quirky & Fun']
    },
    {
      title: "Any deal-breakers?",
      subtitle: "Things you'd rather not receive (totally okay!)",
      field: 'avoid',
      type: 'textarea',
      placeholder: 'e.g., Allergic to certain materials, prefer not to have candles, already have too many mugs...'
    }
  ];

  const currentQuestion = questions[currentStep];
  const isLastStep = currentStep === questions.length - 1;

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateResponse = (field, value) => {
    setResponses(prev => ({ ...prev, [field]: value }));
  };

  const toggleMultiSelect = (field, option) => {
    setResponses(prev => {
      const current = prev[field] || [];
      const updated = current.includes(option)
        ? current.filter(item => item !== option)
        : [...current, option];
      return { ...prev, [field]: updated };
    });
  };

  if (prelaunch) {
    return <ComingSoon />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFBF7' }}>
        <div className="animate-pulse">
          <Sparkles className="w-12 h-12 mx-auto mb-4" style={{ color: '#9E6752' }} />
          <p style={{ color: '#534145' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (showRecommendations) {
    return (
      <div className="min-h-screen" style={{ background: '#FFFBF7' }}>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#FED7A5' }}>
              <CheckCircle className="w-10 h-10" style={{ color: '#534145' }} />
            </div>
            <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
              Thank You! 🎁
            </h1>
            <p className="text-lg" style={{ color: '#73766A' }}>
              Based on your responses, here are some thoughtful gift ideas that might be perfect for you
            </p>
          </div>

          {recommendations.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
                Personalized Recommendations
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {recommendations.map(product => (
                  <ProductCard key={product.id} product={product} user={null} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 text-center">
            <p className="text-sm" style={{ color: '#73766A' }}>
              Your responses have been shared with {giftGiverName}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen page-enter" style={{ background: '#FFFBF7' }}>
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#FED7A5' }}>
            <Gift className="w-8 h-8" style={{ color: '#534145' }} />
          </div>
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
            Help {giftGiverName} Find the Perfect Gift!
          </h1>
          <p className="text-lg" style={{ color: '#73766A' }}>
            Answer a few fun questions so we can find something you'll truly love
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: '#534145' }}>
              Question {currentStep + 1} of {questions.length}
            </span>
            <span className="text-sm" style={{ color: '#9E6752' }}>
              {Math.round(((currentStep + 1) / questions.length) * 100)}% Complete
            </span>
          </div>
          <div className="h-2 rounded-full" style={{ background: '#E5E5E5' }}>
            <div 
              className="h-2 rounded-full transition-all duration-300"
              style={{ 
                background: '#9E6752',
                width: `${((currentStep + 1) / questions.length) * 100}%`
              }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl p-8 mb-6" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
            {currentQuestion.title}
          </h2>
          <p className="mb-6" style={{ color: '#73766A' }}>
            {currentQuestion.subtitle}
          </p>

          {/* Question Input */}
          {currentQuestion.type === 'textarea' && (
            <Textarea
              value={responses[currentQuestion.field] || ''}
              onChange={(e) => updateResponse(currentQuestion.field, e.target.value)}
              placeholder={currentQuestion.placeholder}
              rows={4}
              className="text-base"
            />
          )}

          {currentQuestion.type === 'multiselect' && (
            <div className="flex flex-wrap gap-3">
              {currentQuestion.options.map(option => {
                const isSelected = (responses[currentQuestion.field] || []).includes(option);
                return (
                  <button
                    key={option}
                    onClick={() => toggleMultiSelect(currentQuestion.field, option)}
                    className="px-4 py-3 rounded-xl transition-all text-sm font-medium"
                    style={{
                      background: isSelected ? '#9E6752' : 'white',
                      color: isSelected ? 'white' : '#534145',
                      border: isSelected ? 'none' : '2px solid #E5E5E5'
                    }}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            onClick={handleBack}
            disabled={currentStep === 0}
            variant="outline"
            style={{
              opacity: currentStep === 0 ? 0.5 : 1,
              pointerEvents: currentStep === 0 ? 'none' : 'auto'
            }}
          >
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={submitting}
            style={{ background: '#9E6752', color: 'white' }}
          >
            {submitting ? 'Submitting...' : isLastStep ? 'Get My Recommendations! 🎁' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}
